import { useFeedback26CoStore } from '@/stores/feedback-26-co';
import type { FeedbackFormMentor } from '@/types';
import { useFeedbackEdition } from '../edition-context';
import { NumericRating } from './NumericRating';

interface OverallExperienceProps {
  mentor: FeedbackFormMentor | null;
}

export function OverallExperience({ mentor }: OverallExperienceProps) {
  const {
    formData,
    setOverallRating,
    setNpsScore,
    setParticipationIntent,
    setMentorRating,
  } = useFeedback26CoStore();
  const { eventName } = useFeedbackEdition();

  return (
    <div className="space-y-10">
      <div>
        <h2 className="mb-2 font-bold text-2xl">Experiencia General</h2>
        <p className="text-muted-foreground">
          Cuéntanos cómo fue tu experiencia en {eventName}.
        </p>
      </div>

      <div className="space-y-4">
        <p className="block font-semibold">
          ¿Cómo calificarías tu experiencia en {eventName}?
        </p>
        <NumericRating
          value={formData.overallRating}
          onChange={setOverallRating}
          options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
          leftLabel="Muy mala"
          rightLabel="Excelente"
        />
      </div>

      <div className="space-y-4">
        <p className="block font-semibold">
          ¿Qué tan probable es que recomiendes Platanus Hack a un amig@?
        </p>
        <NumericRating
          value={formData.npsScore}
          onChange={setNpsScore}
          options={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
          leftLabel="Nada probable"
          rightLabel="Muy probable"
        />
      </div>

      {mentor && (
        <div className="space-y-4">
          <p className="block font-semibold">
            ¿Cómo calificarías tu experiencia con tu mentor{' '}
            {mentor.name ? (
              <span className="text-primary">{mentor.name}</span>
            ) : (
              ''
            )}
            ?
          </p>
          <NumericRating
            value={formData.mentorRating}
            onChange={setMentorRating}
            options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
            leftLabel="Muy mala"
            rightLabel="Excelente"
          />
        </div>
      )}

      <div className="space-y-4">
        <p className="block font-semibold">
          ¿Participarías de nuevo en los próximos dos años?
        </p>
        <div className="space-y-3">
          {[
            { value: 'yes', label: 'Sí, definitivamente' },
            { value: 'maybe', label: 'Tal vez' },
            { value: 'no', label: 'No' },
          ].map((option) => (
            <label
              key={option.value}
              className={`flex cursor-pointer items-center gap-4 rounded-lg border-2 p-4 transition-colors ${
                formData.participationIntent === option.value
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-transparent hover:border-primary/30'
              }`}
            >
              <input
                type="radio"
                name="participationIntent"
                value={option.value}
                checked={formData.participationIntent === option.value}
                onChange={() =>
                  setParticipationIntent(option.value as 'yes' | 'no' | 'maybe')
                }
                className="sr-only"
              />
              <span className="font-medium">{option.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
